/** @file Utiltiy functions for working with Quiz answer files. */
import { Maybe } from 'purify-ts';
import { expr, sort, string } from '@codegrade/utils';
/**
 * Highlight all Coding question answers of an answers file, and calculate the line offset for each answer.
 *
 * Each Multiple Choice and Select All question is counted as 1 single line.
 *
 * @param answers The answers file uploaded by a student.
 * @returns An ordered list of highlighted Coding question answers. The list is ordered by question id.
 */
export function splitCodingQuestionLines({ answers }) {
    const sortedAnswers = sort.sortBy(Object.entries(answers), ([id]) => [id]);
    let offset = 0;
    return Maybe.mapMaybe(([questionId, answerObj]) => expr.match(answerObj, 'tag', {
        mcq: () => {
            offset += 1;
            return Maybe.empty();
        },
        'select-all-question': () => {
            offset += 1;
            return Maybe.empty();
        },
        'coding-question': ({ answer, language }) => {
            const lines = string.splitLines(answer);
            if (lines.length > 0 && lines[lines.length - 1] === '') {
                lines.pop();
            }
            const result = {
                questionId,
                offset,
                lines,
                language,
            };
            offset += Math.max(1, lines.length);
            return Maybe.of(result);
        },
    }), sortedAnswers);
}
/**
 * Get the language to use for a coding question.
 *
 * The language identifiers returned here correspond to HighlightJS language identifiers.
 *
 * Note: the extensions checked here should match the file extensions set on the supported
 * implementations of the JPlag `Language`.
 * See plagiarism/jplag-runner/src/main/java/com/codegrade/jplag_runner/languages/QuizLanguage.java
 * for a list of supported languages.
 *
 * @param step The Coding question step.
 * @returns The language to use for this step.
 */
export function getCodingQuestionLanguage(step) {
    const ext = step.studentFileName.split('.').pop();
    switch (ext?.toLocaleLowerCase()) {
        case 'c':
        case 'cc':
        case 'cpp':
        case 'cxx':
        case 'h':
        case 'hh':
        case 'hpp':
        case 'hxx':
            return 'cpp';
        case 'cs':
            return 'csharp';
        case 'java':
            return 'java';
        case 'es':
        case 'esm':
        case 'js':
        case 'jsm':
            return 'javascript';
        case 'php':
            return 'php';
        case 'r':
            return 'r';
        case 'sc':
        case 'scala':
            return 'scala';
        case 'py':
        default:
            return 'python';
    }
}
/**
 * Set the language to use for each coding question answer in an answers file.
 *
 * @param answersFile The answers file to update.
 * @param config The AutoTest configuration.
 * @returns The updated answers file.
 */
export function updateCodingQuestionAnswers(answersFile, config) {
    const answers = { ...answersFile.answers };
    for (const step of config.steps) {
        if (step.type !== 'coding-question') {
            continue;
        }
        const answer = answers[step.id];
        if (answer == null || answer.tag !== 'coding-question') {
            continue;
        }
        answers[step.id] = {
            ...answer,
            language: Maybe.of(getCodingQuestionLanguage(step)),
        };
    }
    return { answers };
}
